import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Collapse,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import IconButton from "@mui/material/IconButton";
import ArrowLeftIcon from "@mui/icons-material/ArrowLeft";

import { fetchAssessmentReportdetailsforReview as fetchAssessmentDetailsService } from "services/modules/cap/standardservice";
import { updateRejectedAssessmentComments as updateRejectedAssessmentCommentsService } from "services/modules/cap/assessmentservice";

import { useNotification } from "config/Notification";
import LoadingOverlay from "components/LoadingOverlay.jsx";

// Type-aware answer display — same as ReviewAssessmentPage
import QuestionRenderer from "../../components/shared/QuestionRenderer";

const RejectAssessmentPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const urlparams = location.state || {};

  const { openNotification } = useNotification();

  const [assessmentsList, setAssessmentsList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedRow, setExpandedRow] = useState(null);
  const [mitigation, setMitigation] = useState({});
  const [overallSummary, setOverallSummary] = useState("");

  const handleInputChange = (event, submissionID) => {
    const { value } = event.target;
    setMitigation((prevState) => ({ ...prevState, [submissionID]: value }));
  };

  const handleRowClick = (rowId) => {
    setExpandedRow((prevRow) => (prevRow === rowId ? null : rowId));
  };

  useEffect(() => {
    if (urlparams.assessmentid !== null) {
      fetchPendingAssessmentsData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchPendingAssessmentsData = async () => {
    setLoading(true);
    try {
      let params = { assessmentid: urlparams.assessmentid };
      const response = await fetchAssessmentDetailsService({ params });
      setAssessmentsList(response.data.controls || []);
    } catch (error) {
      openNotification("Error fetching pending assessments", "error");
    } finally {
      setLoading(false);
    }
  };

  const submitResponse = async () => {
    try {
      let itemlist = [];
      for (let i = 0; i < assessmentsList.length; i++) {
        for (let j = 0; j < assessmentsList[i].subcontrols.length; j++) {
          let comments =
            mitigation[assessmentsList[i].subcontrols[j].submissionID];
          if (comments && comments.trim() !== "") {
            itemlist.push({
              submissionId: assessmentsList[i].subcontrols[j].submissionID,
              assessment_comments: comments,
            });
          }
        }
      }

      if (overallSummary.trim() === "") {
        openNotification("Overall rejection summary is required", "error");
        return;
      }

      if (itemlist.length === 0) {
        openNotification(
          "Please enter at least one subcontrol rejection remark",
          "error",
        );
        return;
      }

      let assessment_id = urlparams.assessmentid;
      if (assessment_id) {
        let response = await updateRejectedAssessmentCommentsService({
          assessment_id,
          overall_rejection_remarks: overallSummary,
          data: itemlist,
        });
        if (response.status === 201) {
          openNotification("Rejected successfully.", "success");
          navigate("/cap/pending-assessment");
        }
      }
    } catch (error) {
      openNotification("Error while submitting the response", "error");
    }
  };

  return (
    <div>
      {/* ── Page header ── */}
      <div className="row py-1">
        <p>
          <img
            className="px-1 icons-style"
            src="../assets/icons/003-clipboard.svg"
            alt="View"
          />
          <b className="px-1">Reject assessment </b>
          <img
            className="px-1 icons-style"
            src="../assets/icons/right-layer.svg"
            alt="View"
          />
          {urlparams.standard === "CIS" && (
            <img
              className="px-sm standard-icon-size"
              src="../assets/icons/standard-cis.png"
              alt="cis logo"
              draggable="false"
            />
          )}
          {urlparams.standard === "ISO-27001" && (
            <img
              className="px-sm standard-icon-size"
              src="../assets/icons/standard-iso-27001.png"
              alt="iso-logo"
              draggable="false"
            />
          )}
          {urlparams.standard === "NIST" && (
            <img
              className="px-sm standard-icon-size"
              src="../assets/icons/standard-nist.png"
              alt="nist logo"
              draggable="false"
            />
          )}
          {urlparams.standard === "COBIT" && (
            <img
              className="px-sm standard-icon-size"
              src="../assets/icons/standard-cobit.png"
              alt="cobit logo"
              draggable="false"
            />
          )}
          {urlparams.standard === "TISAX" && (
            <img
              className="px-sm standard-icon-size"
              src="../assets/icons/standard-tisax.png"
              alt="tisax logo"
              draggable="false"
            />
          )}
          {urlparams.standard === "NIS 2.0" && (
            <img
              className="px-sm standard-icon-size"
              src="../assets/icons/standard-nis2.png"
              alt="nis 2.0 logo"
              draggable="false"
            />
          )}
          {urlparams.standard === "Ransomware" && (
            <img
              className="px-sm standard-icon-size"
              src="../assets/icons/standard-ransomware.png"
              alt="ransomware logo"
              draggable="false"
            />
          )}
          {urlparams.standard === "Data Protection" && (
            <img
              className="px-sm standard-icon-size"
              src="../assets/icons/standard-data-protection.png"
              alt="data protection logo"
              draggable="false"
            />
          )}
          {urlparams.standard === "Supplier Risk" && (
            <img
              className="px-sm standard-icon-size"
              src="../assets/icons/standard-supplier-risk.png"
              alt="supplier risk logo"
              draggable="false"
            />
          )}
          <span> {urlparams.standard} </span> -{" "}
          <span>{urlparams.assessmentid}</span>
        </p>
      </div>

      {loading && <LoadingOverlay />}

      <div>
        {assessmentsList.length > 0 && (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell colSpan={3}>Control name</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {assessmentsList.map((item) => (
                  <React.Fragment key={item.controlID}>
                    {/* ── Outer control row ── */}
                    <TableRow>
                      <TableCell>
                        <IconButton
                          className="expand-row"
                          size="small"
                          onClick={() => handleRowClick(item.controlID)}
                        >
                          {expandedRow === item.controlID ? (
                            <KeyboardArrowUpIcon />
                          ) : (
                            <KeyboardArrowDownIcon />
                          )}
                        </IconButton>
                      </TableCell>
                      <TableCell>
                        {item.controlID + " : " + item.controlName}
                      </TableCell>
                      <TableCell>{item.assessment_id}</TableCell>
                    </TableRow>

                    {/* ── Expanded subcontrols row ── */}
                    <TableRow>
                      <TableCell colSpan={3} sx={{ p: 0 }}>
                        <Collapse
                          in={expandedRow === item.controlID}
                          timeout="auto"
                          unmountOnExit
                        >
                          {/* tableLayout fixed so percentage widths are enforced */}
                          <Table sx={{ tableLayout: "fixed", width: "100%" }}>
                            <TableHead>
                              <TableRow>
                                <TableCell sx={{ width: "7%" }}>Ref</TableCell>
                                <TableCell sx={{ width: "23%" }}>
                                  Control Question
                                </TableCell>
                                <TableCell sx={{ width: "20%" }}>
                                  Question Response
                                </TableCell>
                                <TableCell sx={{ width: "20%" }}>
                                  Rationale for rating
                                </TableCell>
                                <TableCell sx={{ width: "30%" }}>
                                  Reject remarks
                                </TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {item.subcontrols.map((subcontrol) => (
                                <TableRow key={subcontrol.subcontrolID}>
                                  {/* Ref */}
                                  <TableCell
                                    sx={{
                                      width: "7%",
                                      whiteSpace: "nowrap",
                                      verticalAlign: "top",
                                    }}
                                  >
                                    {subcontrol.subcontrol}
                                  </TableCell>

                                  {/* Control Question */}
                                  <TableCell
                                    sx={{
                                      width: "23%",
                                      verticalAlign: "top",
                                      wordBreak: "break-word",
                                    }}
                                  >
                                    <div className="subcontrols">
                                      {subcontrol.subcontrolName}
                                    </div>
                                  </TableCell>

                                  {/* Answer Given — QuestionRenderer in readOnly mode, same as ReviewAssessmentPage */}
                                  <TableCell
                                    sx={{ width: "20%", verticalAlign: "top" }}
                                  >
                                    <QuestionRenderer
                                      question_type={
                                        subcontrol.question_type ||
                                        "five_option"
                                      }
                                      answer_config={
                                        subcontrol.answer_config || null
                                      }
                                      value={
                                        subcontrol.answer_value ||
                                        subcontrol.severity
                                      }
                                      readOnly={true}
                                    />
                                  </TableCell>

                                  {/* Rationale */}
                                  <TableCell
                                    sx={{
                                      width: "20%",
                                      verticalAlign: "top",
                                      wordBreak: "break-word",
                                    }}
                                    className="subcontrols"
                                  >
                                    {subcontrol.rational}
                                  </TableCell>

                                  {/* Reject remarks textarea */}
                                  <TableCell
                                    sx={{ width: "30%", verticalAlign: "top" }}
                                  >
                                    <textarea
                                      key={subcontrol.submissionID}
                                      id={subcontrol.submissionID}
                                      className="mitigation rationale"
                                      value={
                                        mitigation[subcontrol.submissionID] ||
                                        ""
                                      }
                                      onChange={(e) =>
                                        handleInputChange(
                                          e,
                                          subcontrol.submissionID,
                                        )
                                      }
                                      placeholder="Reject remarks"
                                      style={{
                                        width: "100%",
                                        minHeight: "80px",
                                        resize: "vertical",
                                      }}
                                    />
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* ── Overall rejection summary ── */}
        {assessmentsList.length > 0 && (
          <div className="mt-4">
            <div
              className="card shadow-sm p-3"
              style={{ border: "1px solid #e3e6f0", width: "100%" }}
            >
              <label className="font-weight-bold text-primary">
                Overall Rejection Summary (Max 500 characters)
                <span
                  style={{ color: "red", fontSize: "18px", marginLeft: "4px" }}
                >
                  *
                </span>
              </label>
              <textarea
                className="form-control"
                rows="4"
                maxLength="500"
                placeholder="Provide a high-level summary of why this entire assessment is being rejected..."
                value={overallSummary}
                onChange={(e) => setOverallSummary(e.target.value)}
              />
              <div className="text-right mt-1">
                <small
                  className={
                    overallSummary.length >= 500 ? "text-danger" : "text-muted"
                  }
                >
                  {overallSummary.length}/500 characters
                </small>
              </div>
            </div>
          </div>
        )}

        {/* ── Action buttons ── */}
        {assessmentsList.length > 0 && (
          <div className="reject-alignment">
            <button
              className="go-back-button reject-btn"
              onClick={() => navigate("/cap/pending-assessment")}
            >
              <ArrowLeftIcon /> Go back
            </button>
            <button
              className="reject-button reject-btn"
              onClick={() => submitResponse()}
            >
              Reject
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default RejectAssessmentPage;
